export function numberWithCommas(x: number) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// Prices as the application writes them everywhere, rounded to the dollar: the amount, a space,
// then the unit — "12,300 $", "8,200 $/ct". The space is a non-breaking one, so the unit never
// wraps away from its amount (a chart's axis labels break at spaces). Mirrors format_price on the
// pricing backend.
const NO_BREAK_SPACE = " ";

export function formatPrice(x: number) {
  return numberWithCommas(Math.round(x)) + NO_BREAK_SPACE + "$";
}

export function formatPricePerCarat(x: number) {
  return numberWithCommas(Math.round(x)) + NO_BREAK_SPACE + "$/ct";
}

// Pricing colors travel as raw per-system values alongside the system they belong to; this rebuilds
// the query string they are written as. Mirrors format_color_query on the pricing backend, which
// parses the same strings back.
export function formatColorQuery(
  system: string,
  rawValue: string | number,
): string {
  switch (system) {
    case "Melee":
      return `Melee ${rawValue}`;
    case "Flat":
      return `Flat ${rawValue}`;
    case "Langerman":
      return `Langerman ${rawValue}`;
    default:
      return `${rawValue}`;
  }
}

// The one color a pricing report can be run for that belongs to no color system: the average across
// every stone. Mirrors AverageDistributionColor on the backend, which offers it among the colors.
export const AVERAGE_DISTRIBUTION_COLOR = "Average Distribution";

// The color systems in the order a list of colors shows them; a color of none comes last.
const COLOR_SYSTEM_ORDER = ["GIA", "Langerman", "Flat", "Melee"];

// The color system a query string belongs to, the reverse of formatColorQuery; null for the one
// color of no system.
export function colorQuerySystem(colorQuery: string): string | null {
  if (colorQuery === AVERAGE_DISTRIBUTION_COLOR) return null;
  if (colorQuery.startsWith("Melee ")) return "Melee";
  if (colorQuery.startsWith("Flat ")) return "Flat";
  if (colorQuery.startsWith("Langerman ")) return "Langerman";
  return "GIA";
}

// Orders color query strings as the color lists show them: grouped by system, GIA first, then
// alphabetically within a system, numbers by value ("Flat 9" before "Flat 10").
export function byColorSystemThenName(a: string, b: string): number {
  const systemRank = (colorQuery: string) => {
    const system = colorQuerySystem(colorQuery);
    const index = system ? COLOR_SYSTEM_ORDER.indexOf(system) : -1;
    return index < 0 ? COLOR_SYSTEM_ORDER.length : index;
  };
  return (
    systemRank(a) - systemRank(b) ||
    a.localeCompare(b, undefined, { numeric: true })
  );
}

// The color a pricing was priced for, written as the query string it was asked with — the name it
// goes by on screen, and the color a link prices again. A pricing names its color only through its
// equivalent colors, so none means the one color that has none.
export function pricingColorQuery(pricing: {
  equivalentColors?: { system: string; color: string } | null;
}): string {
  const equivalentColors = pricing.equivalentColors;
  return equivalentColors
    ? formatColorQuery(equivalentColors.system, equivalentColors.color)
    : AVERAGE_DISTRIBUTION_COLOR;
}

// A color's confirmed equivalents, written for display. The color itself is never among them.
export function formatEquivalentColors(equivalentColors: {
  system: string;
  equivalentColors: string[];
}): string[] {
  return equivalentColors.equivalentColors.map((rawValue) =>
    formatColorQuery(equivalentColors.system, rawValue),
  );
}

// Dates are shown as YYYY-MM-DD everywhere, in the browser's time; a moment that needs its time too
// adds HH:mm. An absent or unreadable value shows as nothing.
function pad(value: number) {
  return value.toString().padStart(2, "0");
}

export function formatDate(value: string | Date | null | undefined) {
  const date = value instanceof Date ? value : value ? new Date(value) : null;
  if (!date || isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function formatDateTime(value: string | Date | null | undefined) {
  const date = value instanceof Date ? value : value ? new Date(value) : null;
  if (!date || isNaN(date.getTime())) return "";
  return `${formatDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// The services write their own clock — which runs on UTC — into columns that carry no zone, so the
// timestamp arrives with none either. Read as-is it would be taken for the browser's local time and
// shown an offset early, so it is read as UTC. A value that does arrive with a zone is left alone.
export const asUTC = (value: string) =>
  value && !/(Z|[+-]\d\d:?\d\d)$/.test(value) ? value + "Z" : value;
